import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { scrollToId } from '../hooks/useSmoothScroll';
import { getNextNight, formatShortDate, countdownLabel } from '../data/events';
import useEvents from '../hooks/useEvents';
import styles from './Hero.module.css';

/**
 * Hero — branched by breakpoint.
 *
 * Desktop (>= 761px):
 *   full-bleed photo with dark gradient overlay, cream/orange headline
 *   pinned bottom-left, italic tagline beneath. Photo is dominant.
 *
 * Mobile (< 761px):
 *   cream background, huge black headline at top, small centered photo
 *   accent in the middle, italic tagline beneath. Mirrors Social
 *   Dallas's mobile hero pattern.
 *
 * The same component renders both. CSS handles the swap so the photo
 * background, the photo accent, and the layout switch at the breakpoint.
 */
export default function Hero() {
  return (
    <section id="hero" className={styles.hero}>
      {/* Desktop-only photo layer */}
      <div className={styles.photoLayer} aria-hidden="true">
        <img
          src="/images/hero-bg.jpg"
          alt=""
          className={styles.photo}
          loading="eager"
          fetchpriority="high"
          decoding="async"
        />
        <div className={styles.overlay} />
      </div>

      <div className={styles.shell}>
        <h1 className={styles.title}>
          <SliderLine line={{ text: 'Worshipping.' }} index={0} />
          <SliderLine
            line={{ text: 'Everywhere', underline: true, tail: '!' }}
            index={1}
          />
        </h1>

        {/* Mobile-only photo accent */}
        <motion.figure
          className={styles.accent}
          initial={{ opacity: 0, scale: 0.97, y: 14 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.0, ease: [0.22, 1, 0.36, 1] }}
        >
          <img
            src="/images/hero-bg.jpg"
            alt="Worshippers at a Revive Night"
            className={styles.accentImg}
            loading="eager"
            fetchpriority="high"
            decoding="async"
          />
          <figcaption className={styles.accentMark}>
            <img src="/images/favicon-rw.png" alt="RW" />
            <span className={styles.accentDot}>©</span>
          </figcaption>
        </motion.figure>

        <motion.p
          className={styles.tagline}
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.55, delay: 1.3, ease: [0.22, 1, 0.36, 1] }}
        >
          Especially in unlikely places.
        </motion.p>

        <NextGatheringChip />

        <button
          className={styles.scrollCue}
          onClick={() => scrollToId('#nights-strip', -80)}
          aria-label="Scroll to next section"
        >
          <span>Scroll</span>
          <span className={styles.scrollLine} />
        </button>
      </div>
    </section>
  );
}

/**
 * Live "next gathering" chip — the hero's one functional element.
 * Shows the next confirmed night (date · place) and deep-links to /nights.
 * Falls back to a plain Revive Nights link while the next date is TBD,
 * and stays quiet during a Sabbath month.
 */
function NextGatheringChip() {
  const events = useEvents();
  const next = getNextNight(new Date(), events);
  if (!next || next.isSabbath) return null;

  const confirmed = !next.tbd;
  const cd = countdownLabel(next);

  return (
    <motion.div
      className={styles.nextChipWrap}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.55, delay: 1.55, ease: [0.22, 1, 0.36, 1] }}
    >
      <Link to="/nights" className={styles.nextChip}>
        <span className={styles.nextChipPulse} aria-hidden="true" />
        <span className={styles.nextChipLabel}>Next gathering</span>
        <span className={styles.nextChipInfo}>
          {confirmed
            ? `${formatShortDate(next.date)} · ${next.locationShort || next.locationName}`
            : `${next.monthLabel} · details soon`}
        </span>
        {confirmed && cd && <span className={styles.nextChipCount}>{cd}</span>}
        <span className={styles.nextChipArrow} aria-hidden="true">→</span>
      </Link>
    </motion.div>
  );
}

/**
 * Reveal-from-below: clip-mask container, the inner span slides up
 * from y:108% to y:0. Each line gets a 0.3s stagger so they cascade.
 * Underline waits for both lines to settle, then draws left-to-right.
 */
function SliderLine({ line, index }) {
  return (
    <span className={styles.titleLineClip}>
      <motion.span
        className={styles.titleLine}
        initial={{ y: '108%' }}
        animate={{ y: '0%' }}
        transition={{
          duration: 0.7,
          delay: 0.1 + index * 0.18,
          ease: [0.22, 1, 0.36, 1],
        }}
      >
        {line.underline ? (
          <span className={styles.underlineWord}>
            <span className={styles.underlineInner}>{line.text}</span>
            <motion.span
              className={styles.underlineStroke}
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{
                duration: 0.65,
                delay: 0.75,
                ease: [0.22, 1, 0.36, 1],
              }}
            />
          </span>
        ) : (
          line.text
        )}
        {line.tail && <span className={styles.titleTail}>{line.tail}</span>}
      </motion.span>
    </span>
  );
}
